import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, Database, Cookie, Mail, ChevronRight } from "lucide-react";

const Section = ({ icon: Icon, title, children }) => (
  <motion.section
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-8"
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-marigold/20 rounded-lg flex items-center justify-center">
        <Icon className="text-marigold" size={20} />
      </div>
      <h2 className="font-bebas text-2xl text-white tracking-wider">{title}</h2>
    </div>
    <div className="text-gray-300 font-inter text-sm leading-relaxed space-y-3 pl-13">
      {children}
    </div>
  </motion.section>
);

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-night-count">
      {/* Header */}
      <div className="bg-gradient-to-r from-gorkhali-red/20 via-peace-blue/10 to-gorkhali-red/20 py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Shield className="mx-auto mb-4 text-marigold" size={48} />
            <h1 className="font-bebas text-5xl text-white tracking-wider mb-4">
              PRIVACY POLICY
            </h1>
            <p className="text-gray-400 font-inter">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-white/5 border border-white/10 rounded-lg p-6 mb-8"
        >
          <p className="text-gray-300 font-inter leading-relaxed">
            Welcome to Nepali Ballot ("we," "our," or "us"). We are committed to protecting your privacy and ensuring transparency about how we collect, use, and safeguard your information. This Privacy Policy explains our practices regarding data collection on nepaliballot.com (the "Website").
          </p>
        </motion.div>

        <Section icon={Database} title="INFORMATION WE COLLECT">
          <p><strong className="text-white">Anonymous Voting Data:</strong> When you participate in our election simulation, we collect anonymous voting preferences. This data is not linked to any personally identifiable information.</p>
          <p><strong className="text-white">Usage Information:</strong> We automatically collect certain information when you visit our Website, including your IP address (anonymized), browser type, device information, pages visited, and time spent on the site.</p>
          <p><strong className="text-white">Cookies and Similar Technologies:</strong> We use cookies and similar tracking technologies to enhance your experience, analyze site traffic, and for advertising purposes through Google AdSense.</p>
        </Section>

        <Section icon={Eye} title="HOW WE USE YOUR INFORMATION">
          <ul className="list-disc list-inside space-y-2">
            <li>To operate and maintain the election simulation platform</li>
            <li>To display aggregated voting results and statistics</li>
            <li>To improve our Website's functionality and user experience</li>
            <li>To analyze usage patterns and optimize content</li>
            <li>To display relevant advertisements through Google AdSense</li>
            <li>To prevent fraud and ensure platform security</li>
          </ul>
        </Section>

        <Section icon={Cookie} title="COOKIES & ADVERTISING">
          <p>We use cookies for essential site functionality and analytics. Third-party advertisers, including Google AdSense, may use cookies to serve ads based on your prior visits to our Website or other websites.</p>
          <p><strong className="text-white">Google AdSense:</strong> Google uses cookies to serve ads based on your visit to this site and other sites on the Internet. You may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer" className="text-marigold hover:underline">Google Ads Settings</a>.</p>
          <p>You can manage cookie preferences through your browser settings. Note that disabling cookies may affect site functionality.</p>
        </Section>

        <Section icon={Lock} title="DATA SECURITY">
          <p>We implement appropriate technical and organizational security measures to protect your information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Secure HTTPS encryption for all data transmission</li>
            <li>Regular security assessments and updates</li>
            <li>Limited access to data on a need-to-know basis</li>
            <li>Anonymous data collection practices</li>
          </ul>
        </Section>

        <Section icon={Database} title="DATA RETENTION & SHARING">
          <p><strong className="text-white">Retention:</strong> We retain anonymous voting data and analytics for the duration necessary to provide our services and comply with legal obligations.</p>
          <p><strong className="text-white">Third-Party Sharing:</strong> We do not sell your personal information. We may share anonymized, aggregated data with:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Analytics providers (Google Analytics) to understand site usage</li>
            <li>Advertising partners (Google AdSense) to display relevant ads</li>
            <li>Legal authorities when required by law</li>
          </ul>
        </Section>

        <Section icon={Shield} title="YOUR RIGHTS">
          <p>Depending on your location, you may have the following rights:</p>
          <ul className="list-disc list-inside space-y-2 mt-2">
            <li>Access to information we hold about you</li>
            <li>Correction of inaccurate data</li>
            <li>Deletion of your data (where applicable)</li>
            <li>Opt-out of personalized advertising</li>
            <li>Lodge a complaint with a supervisory authority</li>
          </ul>
        </Section>

        <Section icon={Mail} title="CONTACT US">
          <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
          <div className="mt-3 p-4 bg-white/5 rounded-lg">
            <p><strong className="text-white">Email:</strong> buildersadventure00@gmail.com</p>
            <p><strong className="text-white">Website:</strong> <Link to="/contact" className="text-marigold hover:underline">nepaliballot.com/contact</Link></p>
          </div>
        </Section>

        {/* Children's Privacy */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gorkhali-red/10 border border-gorkhali-red/30 rounded-lg p-6 mb-8"
        >
          <h3 className="font-bebas text-xl text-white tracking-wider mb-3">CHILDREN'S PRIVACY</h3>
          <p className="text-gray-300 font-inter text-sm">
            Our Website is designed for general audiences interested in Nepal's democratic process. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, please contact us immediately.
          </p>
        </motion.div>

        {/* Updates */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-white/5 border border-white/10 rounded-lg p-6"
        >
          <h3 className="font-bebas text-xl text-white tracking-wider mb-3">CHANGES TO THIS POLICY</h3>
          <p className="text-gray-300 font-inter text-sm">
            We may update this Privacy Policy periodically. We will notify you of any material changes by posting the new policy on this page with an updated "Last updated" date. Your continued use of the Website after changes constitutes acceptance of the updated policy.
          </p>
        </motion.div>

        {/* Navigation */}
        <div className="mt-12 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/terms" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            Terms of Service <ChevronRight size={16} />
          </Link>
          <Link 
            to="/disclaimer" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            Disclaimer <ChevronRight size={16} />
          </Link>
          <Link 
            to="/contact" 
            className="flex items-center gap-2 text-marigold hover:underline font-inter text-sm"
          >
            Contact Us <ChevronRight size={16} />
          </Link>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black py-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-sm">© 2026 Nepali Ballot • All Rights Reserved</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
